import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import ConfirmDialog from "../components/ConfirmDialog";
import { isBlank } from "../util/ext";
import Paper from "@material-ui/core/Paper";
import { useConfirm } from "../common/use-confirm";
import {
  append,
  difference,
  filter,
  includes,
  pluck,
  prop,
  sortBy,
  without,
} from "ramda";
import Grid from "@material-ui/core/Grid";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@material-ui/core";
import Button from "@material-ui/core/Button";
import { useAddUserRoles, useRoles } from "./roleServices";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import Checkbox from "@material-ui/core/Checkbox";

const useStyles = makeStyles(theme => ({
  form: {
    marginTop: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const AddUserRoleDialog = props => {
  const { open, onCancel, onOk, onError, user } = props;
  const classes = useStyles();

  //-- Manage the confirm dialog
  const confirmHandler = useConfirm();
  const { showConfirm } = confirmHandler;

  const [availableRoles, setAvailableRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);

  const roles = useRoles();
  useEffect(() => {
    console.log("roles setup useEffect...");
    if (roles.isSuccess) {
      setAvailableRoles(
        sortBy(
          prop("roleName"),
          filter(
            r => r.roleType === "USER" || r.roleName === "ROLE_LIBRARY",
            difference(roles.data, user.roles),
          ),
        ),
      );
    }
  }, [roles.isSuccess, roles.data, user.roles]);

  const addUserRoles = useAddUserRoles();
  const handleApply = useCallback(async () => {
    const roleList = pluck("roleName", selectedRoles);
    try {
      await addUserRoles.mutateAsync({
        resourceIds: { id: user.userId },
        data: roleList,
      });
      setSelectedRoles([]);
      onOk();
    } catch (err) {
      onError(err);
    }
  }, [selectedRoles, user.userId, addUserRoles, onOk, onError]);

  const handleClose = useCallback(() => {
    if (isBlank(selectedRoles)) {
      onCancel();
    } else {
      showConfirm(
        "Discard Changes?",
        "You have selected roles without applying.  Do you wish to discard these changes?",
        {
          onConfirm: () => {
            onCancel();
          },
        },
      );
    }
  }, [selectedRoles, showConfirm, onCancel]);

  const handleRoleSelected = role => event => {
    if (event.target.checked) {
      setSelectedRoles(prev => append(role, prev));
    } else {
      setSelectedRoles(prev => without([role], selectedRoles));
    }
  };

  const maxWidth = useMemo(() => {
    return availableRoles.length > 18
      ? false
      : availableRoles.length > 12
      ? "md"
      : "sm";
  }, [availableRoles.length]);

  return (
    <Dialog
      fullWidth
      maxWidth={maxWidth}
      open={open}
      onClose={handleClose}
      aria-labelledby="add-user-roles-dialog-title"
    >
      <DialogTitle id="add-user-roles-dialog-title">Add User Roles</DialogTitle>
      <DialogContent>
        <ConfirmDialog handler={confirmHandler} />
        <Paper>
          <Box className={classes.form}>
            <Grid
              spacing={2}
              container
              direction="column"
              style={{ maxHeight: 300 }}
            >
              {availableRoles.map(role => (
                <Grid key={role.roleId} item md={6}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includes(role, selectedRoles)}
                        onChange={handleRoleSelected(role)}
                        name={role.roleName}
                        color="primary"
                      />
                    }
                    label={role.roleName}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Paper>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="primary">
          Cancel
        </Button>
        <Button onClick={handleApply} color="primary">
          Apply
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserRoleDialog;
